import React, { useEffect, useState } from "react";
import axios from "axios";
import * as Styled from "./styled";
import * as PerStyled from "../styled";
import * as GStyled from "../../styled";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { createUser, loadUsers } from "../../../store/workers/actions";
import { apiService } from "../../../utils";
import { roles } from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { validation } from "../../../utils";
import { addApiMessage } from "../../../store/messageBar/actions";
import { TextField, Button, Modal } from "@6tamp/uikit";
import { useTranslation } from "react-i18next";

const CreateUserModal = ({ isOpen, close }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [locations, setLocations] = useState([]);
  const isLoading = useSelector(({ workers }) => workers.isLoading);

  const loadLocations = async (token) => {
    try {
      const { data } = await apiService.get("/location", {
        cancelToken: token,
      });
      setLocations(data.map(({ title, _id }) => ({ label: title, id: _id })));
    } catch (e) {}
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    loadLocations(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      type: null,
      name: "",
      lastName: "",
      email: "",
      password: "",
      location: null,
      label: "",
    },
    validationSchema: Yup.object({
      type: Yup.object({})
        .typeError(t("core.validation.is_required"))
        .required(t("core.validation.is_required")),
      name: Yup.string().required(t("core.validation.is_required")),
      lastName: Yup.string().required(t("core.validation.is_required")),
      email: Yup.string()
        .email(t("core.validation.email.invalid"))
        .required(t("core.validation.is_required")),
      password: Yup.string()
        .required(t("core.validation.is_required"))
        .min(8, t("core.validation.password.short"))
        .max(32, t("core.validation.password.long"))
        .matches(
          /^[A-Za-z0-9]+$/,
          t("core.validation.password.invalid_simple")
        ),
      location: Yup.object({})
        .typeError(t("core.validation.is_required"))
        .required(t("core.validation.is_required")),
      label: Yup.string()
        .typeError(t("core.validation.is_required"))
        .required(t("core.validation.is_required")),
    }),
    onSubmit: async (values) => {
      const { result, msg } = await dispatch(
        createUser({
          email: values.email,
          name: values.name,
          lastName: values.lastName,
          password: values.password,
          type: values.type.id,
          location: values.location.id,
        })
      );
      dispatch(addApiMessage(result ? "success" : "alert", msg));
      if (result) {
        dispatch(loadUsers(null, "active"));
        formik.resetForm();
      }
      close();
    },
  });

  const onClose = () => {
    close();
    formik.resetForm();
  };

  // Инициализаця локации если она одна в дропдаун
  useEffect(() => {
    const isSingle = locations?.length === 1;
    formik.setFieldValue("location", isSingle ? locations[0] : null, true);
    formik.setFieldValue("label", isSingle ? locations[0].label : null, true);
  }, [locations]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("views.workers.create_user_modal_title")}
      description={t("views.workers.create_user_modal_desc")}
    >
      <Styled.Form
        onSubmit={formik.handleSubmit}
        onChange={formik.handleChange}
      >
        <Styled.InputsContainer>
          <div>
            <PerStyled.Select
              value={formik.values.type}
              id={"type"}
              name={"type"}
              placeholder={t("views.workers.fields_placeholders.type")}
              items={roles.map((role) => ({
                label: t(role.labelId),
                id: role.id,
              }))}
              state={validation.isError(formik, "type") ? "alert" : ""}
              onChange={({ value }) => formik.setFieldValue("type", value)}
            />
            {validation.isError(formik, "type") && (
              <GStyled.ErrorMessage>{formik.errors.type}</GStyled.ErrorMessage>
            )}
          </div>
          <div>
            <PerStyled.Select
              value={formik.values.location}
              id={"location"}
              name={"location"}
              placeholder={t("views.workers.fields_placeholders.location")}
              items={locations}
              state={validation.isError(formik, "label") ? "alert" : ""}
              onChange={({ value }) => {
                formik.setFieldValue("location", value, true);
                formik.setFieldValue("label", value.label, true);
              }}
            />
            {validation.isError(formik, "label") && (
              <GStyled.ErrorMessage>{formik.errors.label}</GStyled.ErrorMessage>
            )}
          </div>
          <TextField
            id={"name"}
            value={formik.values.name}
            name={"name"}
            width={"full"}
            height={40}
            state={validation.isError(formik, "name") ? "error" : ""}
            placeholder={t("views.workers.fields_placeholders.name")}
            errorText={formik.errors.name}
          />
          <TextField
            id={"lastName"}
            value={formik.values.lastName}
            name={"lastName"}
            width={"full"}
            height={40}
            state={validation.isError(formik, "lastName") ? "error" : ""}
            placeholder={t("views.workers.fields_placeholders.last_name")}
            errorText={formik.errors.lastName}
          />
          <TextField
            id={"email"}
            value={formik.values.email}
            name={"email"}
            width={"full"}
            height={40}
            state={validation.isError(formik, "email") ? "error" : ""}
            placeholder={t("core.inputs_defs.email.label")}
            errorText={formik.errors.email}
            autoComplete={"new-password"}
          />
          <TextField
            id={"password"}
            value={formik.values.password}
            name={"password"}
            width={"full"}
            height={40}
            password={true}
            state={validation.isError(formik, "password") ? "error" : ""}
            placeholder={t("core.inputs_defs.password.label")}
            errorText={formik.errors.password}
            autoComplete={"new-password"}
          />
        </Styled.InputsContainer>
        <PerStyled.ModalButtonWrapper>
          <Button
            type={"submit"}
            round={true}
            width={"full"}
            text={t("views.workers.btn_create_user")}
            isLoading={isLoading}
          />
        </PerStyled.ModalButtonWrapper>
      </Styled.Form>
    </Modal>
  );
};

CreateUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};
export default CreateUserModal;
